import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Checkbox, FormControlLabel, Stack, Divider,
  Tooltip, Switch, InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonIcon from '@mui/icons-material/Person';
import {
  listUsers, listGroups, addUserToGroup, removeUserFromGroup,
  updateUserAttributes, setUserEnabled
} from '../services/api/userAdminService';

const GROUP_ICONS = {
  'slughorn-administrator': { icon: <AdminPanelSettingsIcon fontSize="small" />, color: 'error', label: 'Administrator' },
  'slughorn-moderator':     { icon: <SupervisorAccountIcon fontSize="small" />, color: 'warning', label: 'Moderator' },
  'slughorn-operator':      { icon: <EngineeringIcon fontSize="small" />, color: 'info', label: 'Operator' },
};

const DEFAULT_GROUP = { icon: <PersonIcon fontSize="small" />, color: 'default', label: null };

const READ_ONLY_ATTRS = ['sub', 'identities', 'cognito:username'];

export default function UserAdmin() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [filter, setFilter] = useState('');  
  const [confirmAction, setConfirmAction] = useState(null); // { message, onConfirm }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, g] = await Promise.all([listUsers(), listGroups()]);
      setUsers(u);
      setGroups(g.map(g => g.name));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredUsers = useMemo(() => {
    if (!filter) return users;
    const q = filter.toLowerCase();
    return users.filter(u => u.email.toLowerCase().includes(q) || u.username.toLowerCase().includes(q));
  }, [users, filter]);

  const handleEdit = (user) => {
    const attrs = {};
    Object.entries(user).forEach(([k, v]) => {
      if (!['username', 'groups', 'enabled', 'userStatus', 'created', 'modified'].includes(k)) {
        attrs[k] = v ?? '';
      }
    });
    setEditUser({ ...user, editAttrs: attrs, editGroups: [...user.groups], editEnabled: user.enabled });
    setSaveError(null);
  };

  const requireConfirm = (message, onConfirm) => setConfirmAction({ message, onConfirm });

  const doSave = async () => {
    setSaving(true);
    setSaveError(null);
    const original = users.find(u => u.username === editUser.username);

    // Build the optimistic updated user
    const updatedUser = {
      ...original,
      enabled: editUser.editEnabled,
      groups: editUser.editGroups,
      ...Object.fromEntries(
        Object.entries(editUser.editAttrs).filter(([k]) => !READ_ONLY_ATTRS.includes(k))
      )
    };

    // Apply optimistic update immediately
    setUsers(prev => prev.map(u => u.username === updatedUser.username ? updatedUser : u));
    setEditUser(null);

    try {
      const changedAttrs = Object.entries(editUser.editAttrs)
        .filter(([k, v]) => !READ_ONLY_ATTRS.includes(k) && String(v) !== String(original[k] ?? ''))
        .map(([Name, Value]) => ({ Name, Value: String(Value) }));

      if (changedAttrs.length > 0) {
        await updateUserAttributes(editUser.username, changedAttrs);
      }

      if (editUser.editEnabled !== original.enabled) {
        await setUserEnabled(editUser.username, editUser.editEnabled);
      }

      const toAdd = editUser.editGroups.filter(g => !original.groups.includes(g));
      const toRemove = original.groups.filter(g => !editUser.editGroups.includes(g));
      await Promise.all([
        ...toAdd.map(g => addUserToGroup(editUser.username, g)),
        ...toRemove.map(g => removeUserFromGroup(editUser.username, g)),
      ]);
    } catch (e) {
      // Roll back optimistic update on failure
      setUsers(prev => prev.map(u => u.username === original.username ? original : u));
      setEditUser({ ...editUser });
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const original = users.find(u => u.username === editUser.username);
    const disabling = original.enabled && !editUser.editEnabled;
    const removingAdmin = original.groups.includes('slughorn-administrator') &&
      !editUser.editGroups.includes('slughorn-administrator');

    if (disabling || removingAdmin) {
      const warnings = [
        disabling && 'disable this account',
        removingAdmin && 'remove administrator access',
      ].filter(Boolean).join(' and ');
      requireConfirm(`Are you sure you want to ${warnings} for ${editUser.email}?`, doSave);
    } else {
      doSave();
    }
  };

  const toggleGroup = (groupName) => {
    setEditUser(prev => ({
      ...prev,
      editGroups: prev.editGroups.includes(groupName)
        ? prev.editGroups.filter(g => g !== groupName)
        : [...prev.editGroups, groupName]
    }));
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Cognito Users ({filteredUsers.length}{filter ? ` of ${users.length}` : ''})</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Filter by email or username"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 280 }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', flexGrow: 1, overflow: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Groups</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map(user => (
              <TableRow
                key={user.username}
                hover
                onClick={() => handleEdit(user)}
                sx={{ '& td': { py: 0, lineHeight: 1 }, cursor: 'pointer' }}
              >
                <TableCell>{user.email}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{user.username}</TableCell>
                <TableCell>
                  <Tooltip title={user.userStatus}>
                    {user.userStatus === 'CONFIRMED'
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <HourglassEmptyIcon fontSize="small" color="warning" />}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={user.enabled ? 'Enabled' : 'Disabled'}>
                    {user.enabled
                      ? <CheckCircleIcon fontSize="small" color="success" />
                      : <BlockIcon fontSize="small" color="error" />}
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {user.groups.map(g => {
                      const { icon, color, label } = GROUP_ICONS[g] || { ...DEFAULT_GROUP, label: g };
                      return (
                        <Tooltip key={g} title={label || g}>
                          <Box sx={{ color: `${color}.main`, display: 'flex' }}>{icon}</Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {new Date(user.created).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {editUser?.email}</DialogTitle>
        <DialogContent dividers>
          {editUser && (
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editUser.editEnabled}
                    onChange={e => setEditUser(prev => ({ ...prev, editEnabled: e.target.checked }))}
                  />
                }
                label={editUser.editEnabled ? 'Account Enabled' : 'Account Disabled'}
              />

              <Divider>Attributes</Divider>

              {Object.entries(editUser.editAttrs).map(([key, value]) => (
                <TextField
                  key={key}
                  label={key}
                  value={value}
                  size="small"
                  fullWidth
                  disabled={READ_ONLY_ATTRS.includes(key)}
                  helperText={READ_ONLY_ATTRS.includes(key) ? 'Read-only' : undefined}
                  onChange={e => setEditUser(prev => ({
                    ...prev,
                    editAttrs: { ...prev.editAttrs, [key]: e.target.value }
                  }))}
                />
              ))}

              <Divider>Groups</Divider>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {groups.map(g => (
                  <FormControlLabel
                    key={g}
                    control={
                      <Checkbox
                        checked={editUser.editGroups.includes(g)}
                        onChange={() => toggleGroup(g)}
                        size="small"
                      />
                    }
                    label={g}
                  />
                ))}
              </Box>

              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>{confirmAction?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setConfirmAction(null); confirmAction.onConfirm(); }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
